import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    Stack,
    Divider,
    Tooltip,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HotelIcon from "@mui/icons-material/Hotel";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const PackageCard = ({ packageData, selectedCurrency = "GBP", currencyRates = {}, currencySymbols = {} }) => {
    const [isFlightDialogOpen, setIsFlightDialogOpen] = useState(false);
    const {
        "Package Name": packageName,
        Provider: provider,
        "Starting Price": priceSAR,
        Duration: duration,
        "Duration_": durationNumeric,
        "Start Date": startDate,
        "End Date": endDate,
        Category: category,
        "Madinah Hotel Name": madinahHotel,
        "Madinah Hotel Zone": madinahZone,
        "Makkah Hotel Name": makkahHotel,
        "Makkah Hotel Zone": makkahZone,
        "Makkah 2 Hotel Name": makkah2Hotel,
        "Makkah 2 Hotel Zone": makkah2Zone,
        "Madinah Start Date": madinahStart,
        "Madinah End Date": madinahEnd,
        "Makkah Start Date": makkahStart,
        "Makkah End Date": makkahEnd,
        "Makkah 2 Start Date": makkah2Start,
        "Makkah 2 End Date": makkah2End,
        "Madinah Days": madinahDays,
        "Makkah Days": makkahDays,
        "Makkah 2 Days": makkah2Days,
        PackageLink_,
        campsSummary,
        flightsSummary,
        campSelectedName,
    } = packageData;
    const phase2StatusRaw = packageData?.phase2Status || packageData?.["Phase 2 Status"] || "";
    const normalizedPhase2Status = String(phase2StatusRaw).toLowerCase().replace(/\s+/g, "_");
    const isSoldOut = normalizedPhase2Status === "sold_out" || normalizedPhase2Status === "soldout";

    // Helper to format text to Title Case
    const titleCase = (str) => {
        if (!str) return "";
        return str.toLowerCase().split(' ').map(word => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    };

    const formatMoney = (value) => {
        if (value === null || value === undefined || value === '') return '';
        const numeric = parseFloat(String(value).replace(/[^0-9.]/g, ''));
        if (Number.isNaN(numeric)) return '';
        return numeric.toFixed(2);
    };

    const parseDateValue = (dateStr) => {
        if (!dateStr) return null;
        const parsed = new Date(dateStr);
        if (!Number.isNaN(parsed.getTime())) return parsed;

        const match = String(dateStr).match(/(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
        if (!match) return null;

        const months = {
            Jan: "01",
            Feb: "02",
            Mar: "03",
            Apr: "04",
            May: "05",
            Jun: "06",
            Jul: "07",
            Aug: "08",
            Sep: "09",
            Oct: "10",
            Nov: "11",
            Dec: "12",
        };
        const day = match[1].padStart(2, "0");
        const month = months[match[2]] || match[2];
        const year = match[3];
        const rebuilt = new Date(`${year}-${month}-${day}`);
        return Number.isNaN(rebuilt.getTime()) ? null : rebuilt;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = parseDateValue(dateStr);
        if (!date) return dateStr;

        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });

        return `${day} ${month}`;
    };

    const computeDays = (start, end) => {
        const startDate = parseDateValue(start);
        const endDate = parseDateValue(end);
        if (!startDate || !endDate) return "";
        const diff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
        return diff >= 0 ? diff : "";
    };

    const accommodationsList = packageData?.roomSelection?.accommodations || [];

    const normalizeText = (value) => (value || "").toString().trim().toLowerCase();

    const resolveAccommodationCity = (accommodation) => {
        const title = normalizeText(accommodation?.accommodationTitle || accommodation?.hotelName || "");
        if (title.includes("madinah")) return "Madinah";
        if (title.includes("makkah 2") || title.includes("makkah hotel 2")) return "Makkah 2";
        if (title.includes("makkah")) return "Makkah";
        return "Stay";
    };

    const extractRoomTypeAvailability = (accommodation) => {
        if (accommodation?.roomTypeAvailability) {
            return accommodation.roomTypeAvailability;
        }
        const availability = { single: false, double: false, triple: false, quad: false };
        (accommodation?.rooms || []).forEach((room) => {
            const type = room?.roomTypeCategory || '';
            if (type && availability[type] !== undefined) {
                availability[type] = availability[type] || Boolean(room?.available);
            }
        });
        return availability;
    };

    const extractRoomTypePricing = (accommodation) => {
        const pricing = { single: null, double: null, triple: null, quad: null };
        (accommodation?.rooms || []).forEach((room) => {
            const type = room?.roomTypeCategory || '';
            if (!type || pricing[type] !== null) return;
            const value = room?.displayPrice ?? room?.totalPrice ?? room?.dataPrice;
            const numeric = parseFloat(String(value).replace(/[^0-9.]/g, ''));
            if (!Number.isFinite(numeric) || numeric <= 0) return;
            pricing[type] = numeric;
        });
        return pricing;
    };

    const findAccommodationForStay = (stay) => {
        if (!accommodationsList.length) return null;
        const stayHotel = normalizeText(stay?.hotel);
        const stayCity = normalizeText(stay?.city);

        let match = null;
        if (stayHotel) {
            match = accommodationsList.find((acc) => {
                const hotelName = normalizeText(acc?.hotelName);
                const title = normalizeText(acc?.accommodationTitle);
                return hotelName === stayHotel || title === stayHotel;
            });
        }

        if (!match && stayHotel) {
            match = accommodationsList.find((acc) => {
                const hotelName = normalizeText(acc?.hotelName);
                const title = normalizeText(acc?.accommodationTitle);
                return hotelName.includes(stayHotel)
                    || title.includes(stayHotel)
                    || stayHotel.includes(hotelName)
                    || stayHotel.includes(title);
            });
        }

        if (!match && stayCity) {
            match = accommodationsList.find((acc) => normalizeText(resolveAccommodationCity(acc)) === stayCity);
        }

        return match;
    };

    const findRoomMetaForStay = (stay) => {
        const accommodation = findAccommodationForStay(stay);
        if (!accommodation) {
            return { availability: null, pricing: null };
        }
        return {
            availability: extractRoomTypeAvailability(accommodation),
            pricing: extractRoomTypePricing(accommodation),
        };
    };

    const roomTypes = [
        { key: "single", label: "Single" },
        { key: "double", label: "Double" },
        { key: "triple", label: "Triple" },
        { key: "quad", label: "Quad" },
    ];

    const renderRoomTypeBadges = (availability, pricing) => {
        const showConvertedRow = roomTypes.some(({ key }) =>
            availability?.[key] && Number.isFinite(pricing?.[key]) && currencyRates[selectedCurrency] && selectedCurrency !== "SAR"
        );
        return (
            <Box component="table" sx={{ mt: 0.3, borderCollapse: "collapse", width: "100%" }}>
                <Box component="tbody">
                    {/* Row 1: Labels */}
                    <Box component="tr">
                        {roomTypes.map(({ key, label }) => (
                            <Box component="td" key={key} sx={{ py: "0.2px", px: 0, textAlign: "center", width: "25%" }}>
                                <Typography variant="caption" sx={{
                                    fontSize: "0.55rem",
                                    fontWeight: 700,
                                    color: availability?.[key] ? "#166534" : "#94a3b8",
                                    lineHeight: 1,
                                    whiteSpace: "nowrap",
                                }}>
                                    {label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    {/* Row 2: SAR prices */}
                    <Box component="tr">
                        {roomTypes.map(({ key }) => {
                            const available = availability?.[key];
                            const hasPrice = available && Number.isFinite(pricing?.[key]);
                            return (
                                <Box component="td" key={key} sx={{ py: "0.2px", px: 0, textAlign: "center" }}>
                                    <Typography variant="caption" sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        color: available ? "#166534" : "#94a3b8",
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                    }}>
                                        {hasPrice
                                            ? <>{sarSymbol} {pricing[key].toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</>
                                            : "—"}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    {/* Row 3: Converted prices */}
                    {showConvertedRow && (
                        <Box component="tr">
                            {roomTypes.map(({ key }) => {
                                const available = availability?.[key];
                                const hasPrice = available && Number.isFinite(pricing?.[key]);
                                const show = hasPrice && currencyRates[selectedCurrency] && selectedCurrency !== "SAR";
                                return (
                                    <Box component="td" key={key} sx={{ py: "0.2px", px: 0, textAlign: "center" }}>
                                        {show && (
                                            <Typography variant="caption" sx={{
                                                fontSize: "0.73rem",
                                                fontWeight: 600,
                                                color: "#64748b",
                                                lineHeight: 1,
                                                whiteSpace: "nowrap",
                                            }}>
                                                {(currencySymbols[selectedCurrency] || selectedCurrency)}
                                                {selectedCurrency === 'JPY'
                                                    ? Math.round(pricing[key] * currencyRates[selectedCurrency]).toLocaleString()
                                                    : (pricing[key] * currencyRates[selectedCurrency]).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    // Determine Order of Stays
    const stays = [];

    const parseDate = (dateStr) => {
        const parsed = parseDateValue(dateStr);
        return parsed ? parsed.getTime() : 0;
    };

    if (makkahHotel) {
        const stay = {
            city: "Makkah",
            hotel: makkahHotel,
            zone: makkahZone,
            start: formatDate(makkahStart),
            end: formatDate(makkahEnd),
            days: makkahDays || computeDays(makkahStart, makkahEnd),
            timestamp: parseDate(makkahStart)
        };
        const roomMeta = findRoomMetaForStay(stay);
        stay.availability = roomMeta.availability;
        stay.pricing = roomMeta.pricing;
        stays.push(stay);
    }

    if (madinahHotel) {
        const stay = {
            city: "Madinah",
            hotel: madinahHotel,
            zone: madinahZone,
            start: formatDate(madinahStart),
            end: formatDate(madinahEnd),
            days: madinahDays || computeDays(madinahStart, madinahEnd),
            timestamp: parseDate(madinahStart)
        };
        const roomMeta = findRoomMetaForStay(stay);
        stay.availability = roomMeta.availability;
        stay.pricing = roomMeta.pricing;
        stays.push(stay);
    }

    if (makkah2Hotel) {
        const stay = {
            city: "Makkah 2",
            hotel: makkah2Hotel,
            zone: makkah2Zone,
            start: formatDate(makkah2Start),
            end: formatDate(makkah2End),
            days: makkah2Days || computeDays(makkah2Start, makkah2End),
            timestamp: parseDate(makkah2Start)
        };
        const roomMeta = findRoomMetaForStay(stay);
        stay.availability = roomMeta.availability;
        stay.pricing = roomMeta.pricing;
        stays.push(stay);
    }

    if (stays.length === 0 && accommodationsList.length > 0) {
        accommodationsList.forEach((accommodation) => {
            const title = accommodation?.accommodationTitle || "";
            const hotel = accommodation?.hotelName || title || "Accommodation";
            const titleLower = title.toLowerCase();
            let city = "Stay";
            if (titleLower.includes("madinah")) {
                city = "Madinah";
            } else if (titleLower.includes("makkah 2") || titleLower.includes("makkah hotel 2")) {
                city = "Makkah 2";
            } else if (titleLower.includes("makkah")) {
                city = "Makkah";
            }

            const startRaw = accommodation?.checkIn || "";
            const endRaw = accommodation?.checkOut || "";
            stays.push({
                city,
                hotel,
                zone: accommodation?.zone || "",
                start: formatDate(startRaw),
                end: formatDate(endRaw),
                days: computeDays(startRaw, endRaw),
                timestamp: parseDate(startRaw),
                availability: extractRoomTypeAvailability(accommodation),
                pricing: extractRoomTypePricing(accommodation),
            });
        });
    }

    // Sort by timestamp
    stays.sort((a, b) => a.timestamp - b.timestamp);

    const categoryColors = {
        "Economy": { bg: "#e3f2fd", color: "#1976d2", border: "#90caf9" },
        "Premium": { bg: "#fff3e0", color: "#f57c00", border: "#ffcc80" },
        "Luxury": { bg: "#f3e5f5", color: "#7b1fa2", border: "#ce93d8" },
        "VIP": { bg: "#ffebee", color: "#c62828", border: "#ef9a9a" },
        "Standard": { bg: "#e8f5e9", color: "#388e3c", border: "#a5d6a7" },
    };

    const getCategoryStyle = (cat) => {
        const normalized = cat?.toLowerCase();
        if (normalized?.includes("economy")) return categoryColors.Economy;
        if (normalized?.includes("premium")) return categoryColors.Premium;
        if (normalized?.includes("luxury")) return categoryColors.Luxury;
        if (normalized?.includes("vip")) return categoryColors.VIP;
        return categoryColors.Standard;
    };

    const catStyle = getCategoryStyle(category);

    // Convert SAR to selected currency
    const convertPrice = (sarPrice) => {
        if (!sarPrice || !currencyRates[selectedCurrency]) return 0;
        return sarPrice * currencyRates[selectedCurrency];
    };

    const convertedPrice = convertPrice(priceSAR);
    const currencySymbol = currencySymbols[selectedCurrency] || selectedCurrency;
    const sarSymbol = <span style={{ fontSize: "0.5em" }}>﷼</span>;

    const buildCampSummary = () => {
        if (campsSummary) return campsSummary;
        if (campSelectedName) return campSelectedName;

        const selection = packageData?.campSelection;
        if (!selection) return '';

        const camps = selection.camps || [];
        const missing = selection.missingCamps || [];
        const parts = [];
        camps.forEach((camp) => {
            const price = formatMoney(camp?.totalPrice ?? camp?.displayPrice);
            const name = camp?.name || '';
            if (!name) return;
            parts.push(`${name} (${price || 'N/A'})`);
        });
        missing.forEach((name) => {
            if (!name) return;
            parts.push(`${name} (N/A)`);
        });
        return parts.join(' | ');
    };

    const buildFlightsSummary = () => {
        if (flightsSummary) return flightsSummary;
        const flights = packageData?.flights?.flights || [];
        if (!flights.length) return '';
        return flights.map((flight, index) => {
            const outbound = flight?.outbound || {};
            const departure = outbound.departureCity || outbound.departureAirport || '';
            const arrival = outbound.arrivalCity || outbound.arrivalAirport || '';
            if (!departure && !arrival) {
                return `#${index + 1} Outbound`;
            }
            const route = `${departure} → ${arrival}`.replace(/\s+/g, ' ').trim();
            return `#${index + 1} ${route}`;
        }).join('\n');
    };

    const formatLocationShort = (city, airport) => titleCase(city || airport || '');

    const formatLocationDetail = (city, airport) => {
        const cityName = titleCase(city || '');
        const airportName = titleCase(airport || '');
        if (cityName && airportName && !airportName.toLowerCase().includes(cityName.toLowerCase())) {
            return `${cityName} (${airportName})`;
        }
        return cityName || airportName;
    };

    const sanitizeCampName = (value) => {
        if (!value) return "";
        return String(value).replace(/\s*camp\b/i, "").trim();
    };

    const parseSummaryEntries = (value) => {
        if (!value) return [];
        return String(value)
            .split('|')
            .map((part) => {
                const trimmed = part.trim();
                if (!trimmed) return null;
                const match = trimmed.match(/^(.*?)(?:\s*\((.*?)\))?$/);
                const name = sanitizeCampName(match?.[1]?.trim() || trimmed);
                const price = match?.[2]?.trim() || "";
                const priceValue = price ? parseFloat(String(price).replace(/[^0-9.]/g, "")) : null;
                const available = price ? price.toLowerCase() !== "n/a" : true;
                return { name, price: available ? price : "", priceValue, available };
            })
            .filter(Boolean);
    };

    const buildCampEntries = () => {
        const selection = packageData?.campSelection;
        if (selection?.camps?.length || selection?.missingCamps?.length) {
            const entries = [];
            (selection?.camps || []).forEach((camp) => {
                const name = sanitizeCampName(camp?.name || '');
                if (!name) return;
                const priceValueRaw = camp?.totalPrice ?? camp?.displayPrice;
                const priceValue = parseFloat(String(priceValueRaw).replace(/[^0-9.]/g, ""));
                const price = formatMoney(priceValueRaw);
                entries.push({
                    name,
                    price: price || "",
                    priceValue: Number.isFinite(priceValue) ? priceValue : null,
                    available: camp?.available !== false,
                });
            });
            (selection?.missingCamps || []).forEach((name) => {
                if (!name) return;
                entries.push({ name: sanitizeCampName(name), price: "", priceValue: null, available: false });
            });
            return entries;
        }

        return parseSummaryEntries(buildCampSummary());
    };

    const buildFlightEntries = () => {
        const flights = packageData?.flights?.flights || [];
        if (flights.length) {
            return flights.map((flight, index) => {
                const outbound = flight?.outbound || {};
                const inbound = flight?.inbound || {};
                const departure = titleCase(outbound.departureCity || outbound.departureAirport || '');
                const arrival = titleCase(outbound.arrivalCity || outbound.arrivalAirport || '');
                const route = [departure, arrival].filter(Boolean).join(' → ');
                const shortRoute = [
                    formatLocationShort(outbound.departureCity, outbound.departureAirport),
                    formatLocationShort(outbound.arrivalCity, outbound.arrivalAirport),
                ].filter(Boolean).join(', ');
                const airline = outbound.airline ? titleCase(outbound.airline) : '';
                const flightNumber = outbound.flightNumber ? ` ${outbound.flightNumber}` : '';
                const dateLine = outbound.departureDateTime || outbound.date || '';
                const timeLine = outbound.departureTime && outbound.arrivalTime
                    ? `${outbound.departureTime} → ${outbound.arrivalTime}`
                    : (outbound.departureTime || outbound.arrivalTime || '');

                const inboundDeparture = formatLocationDetail(inbound.departureCity, inbound.departureAirport);
                const inboundArrival = formatLocationDetail(inbound.arrivalCity, inbound.arrivalAirport);
                const inboundRoute = [inboundDeparture, inboundArrival].filter(Boolean).join(' → ');
                const inboundAirline = inbound.airline ? titleCase(inbound.airline) : '';
                // const inboundFlightNumber = inbound.flightNumber ? ` ${inbound.flightNumber}` : '';
                const inboundDateLine = inbound.departureDateTime || inbound.date || '';
                const inboundTimeLine = inbound.departureTime && inbound.arrivalTime
                    ? `${inbound.departureTime} → ${inbound.arrivalTime}`
                    : (inbound.departureTime || inbound.arrivalTime || '');

                return {
                    display: shortRoute || `Flight #${index + 1}`,
                    details: {
                        title: route ? `#${index + 1} ${route}` : `#${index + 1} Outbound`,
                        subTitle: `${airline}${flightNumber}`.trim(),
                        meta: [dateLine, timeLine].filter(Boolean).join(' · '),
                        outbound: {
                            departure: formatLocationDetail(outbound.departureCity, outbound.departureAirport),
                            arrival: formatLocationDetail(outbound.arrivalCity, outbound.arrivalAirport),
                            airline: airline,
                            flightNumber: outbound.flightNumber || '',
                            dateLine,
                            timeLine,
                        },
                        inbound: {
                            departure: inboundDeparture,
                            arrival: inboundArrival,
                            airline: inboundAirline,
                            flightNumber: inbound.flightNumber || '',
                            dateLine: inboundDateLine,
                            timeLine: inboundTimeLine,
                            route: inboundRoute,
                        },
                    }
                };
            });
        }

        const summary = buildFlightsSummary();
        if (!summary) return [];
        return summary
            .split('')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({ display: line, details: { title: line } }));
    };

    const campEntries = buildCampEntries();
    const flightEntries = buildFlightEntries();
    const campDisplayEntries = campEntries.length
        ? campEntries
        : [{ name: "Not available", price: "", available: false }];
    const flightDisplayEntries = flightEntries.length
        ? flightEntries
        : [{ display: "Custom flights", details: null }];

    const outboundDepartures = (() => {
        const flights = packageData?.flights?.flights || [];
        const values = flights
            .map((flight) => formatLocationShort(flight?.outbound?.departureCity, flight?.outbound?.departureAirport))
            .filter(Boolean);
        const unique = [];
        values.forEach((value) => {
            if (!unique.includes(value)) unique.push(value);
        });
        return unique;
    })();
    const outboundDepartureText = outboundDepartures.length
        ? outboundDepartures.join(", ")
        : (flightDisplayEntries[0]?.display || "Custom flights");
    const hasFlightDetails = flightDisplayEntries.some((entry) => Boolean(entry.details));

    const handleOpenFlights = () => {
        if (!hasFlightDetails) return;
        setIsFlightDialogOpen(true);
    };

    const handleCloseFlights = () => {
        setIsFlightDialogOpen(false);
    };

    return (
        <Fade in={true} timeout={500}>
            <Card
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    borderRadius: "20px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                        transform: isSoldOut ? "none" : "translateY(-8px) scale(1.02)",
                        boxShadow: isSoldOut ? "0 4px 20px rgba(0,0,0,0.08)" : `0 12px 40px ${catStyle.color}25`, // 15% opacity using hex alpha
                        borderColor: isSoldOut ? catStyle.border : catStyle.color,
                    },
                    overflow: "hidden",
                    position: "relative",
                    bgcolor: "#ffffff",
                    border: `1px solid ${catStyle.border}`,
                    opacity: isSoldOut ? 0.6 : 1,
                    filter: isSoldOut ? "grayscale(0.2)" : "none",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, ${catStyle.color} 0%, ${catStyle.border} 100%)`,
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: "rgba(211,47,47,0.08)",
                        opacity: isSoldOut ? 1 : 0,
                        pointerEvents: "none",
                        transition: "opacity 0.2s ease",
                    }
                }}
            >
                {/* Content Section */}
                <CardContent sx={{ flexGrow: 1, p: 3, "&:last-child": { pb: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Header Chips */}
                    <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">
                        <Chip
                            label={titleCase(category)}
                            size="small"
                            sx={{
                                height: 28,
                                px: 1,
                                fontSize: "0.75rem",
                                bgcolor: catStyle.bg,
                                color: catStyle.color,
                                fontWeight: 700,
                                borderRadius: "8px",
                                border: `1px solid ${catStyle.border}`,
                                letterSpacing: "0.5px",
                            }}
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                icon={<AccessTimeIcon style={{ fontSize: 14, color: '#002366' }} />}
                                label={`${durationNumeric || duration} Days`}
                                size="small"
                                sx={{
                                    height: 28,
                                    px: 1,
                                    fontSize: "0.75rem",
                                    bgcolor: "rgba(0,35,102,0.08)",
                                    color: "#002366",
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                    border: "1px solid rgba(0,35,102,0.2)",
                                    "& .MuiChip-icon": { marginLeft: "4px" }
                                }}
                            />
                            {isSoldOut && (
                                <Chip
                                    label="Sold Out"
                                    size="small"
                                    sx={{
                                        height: 28,
                                        px: 1,
                                        fontSize: "0.75rem",
                                        bgcolor: "rgba(211,47,47,0.12)",
                                        color: "#b71c1c",
                                        fontWeight: 700,
                                        borderRadius: "8px",
                                        border: "1px solid rgba(211,47,47,0.4)",
                                        "& .MuiChip-icon": { marginLeft: "4px" }
                                    }}
                                    icon={
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                bgcolor: "#d32f2f",
                                                boxShadow: "0 0 6px rgba(211,47,47,0.6)",
                                            }}
                                        />
                                    }
                                />
                            )}
                        </Stack>
                    </Stack>

                    {/* Price Section */}
                    <Box sx={{
                        bgcolor: "rgba(0,35,102,0.04)",
                        borderRadius: "12px",
                        p: 1.5,
                        border: "1px solid rgba(0,35,102,0.1)",
                    }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                            <Box>
                                <Typography variant="caption" sx={{
                                    fontSize: '0.6rem',
                                    color: '#94a3b8',
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    display: "block",
                                    mb: 0.3,
                                }}>
                                    Starting From
                                </Typography>
                                <Typography variant="h6" color="#002366" fontWeight={800} sx={{
                                    lineHeight: 1,
                                    fontSize: '1.3rem',
                                    letterSpacing: "-0.5px",
                                }}>
                                    {currencySymbol}{selectedCurrency === 'JPY'
                                        ? Math.round(convertedPrice).toLocaleString()
                                        : convertedPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{
                                    fontSize: '1.1rem',
                                    color: "#002366",
                                    lineHeight: 1,
                                    textAlign: 'right',
                                }}>
                                    {sarSymbol} {parseFloat(priceSAR || 0).toLocaleString()}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Title and Provider */}
                    <Box>
                        <Tooltip title={titleCase(packageName)} arrow>
                            <Typography
                                gutterBottom
                                variant="h6"
                                component="div"
                                sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.4,
                                    minHeight: "2.0em",
                                    fontSize: "1.05rem",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    mb: 1,
                                    color: "#1e293b",
                                    letterSpacing: "0px",
                                }}
                            >
                                {titleCase(packageName)}
                            </Typography>
                        </Tooltip>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <GroupsIcon sx={{ fontSize: 16, color: "#002366" }} />
                            <Typography variant="overline" sx={{
                                color: "#002366",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                display: 'block',
                                mb: 0,
                                fontSize: '0.75rem',
                                lineHeight: 1,
                                textTransform: "uppercase"
                            }}>
                                {titleCase(provider)}
                            </Typography>
                        </Stack>
                    </Box>

                    <Divider sx={{ borderStyle: "dashed", my: 0.5, borderColor: "rgba(0,0,0,0.08)" }} />

                    {/* Info Grid */}
                    <Stack spacing={1.8}>
                        {/* Dates */}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                                bgcolor: "rgba(0,35,102,0.04)",
                                p: 1.5,
                                borderRadius: "12px",
                                border: "1px solid rgba(0,35,102,0.1)",
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: "#002366",
                                    borderRadius: "8px",
                                    p: 0.8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CalendarTodayIcon sx={{ color: "#fff", fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.9rem', lineHeight: 1.3, color: '#002366' }}>
                                    {formatDate(startDate)} <span style={{ color: "#94a3b8", margin: "0 6px" }}>→</span> {formatDate(endDate)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Ordered Stays */}
                        <Stack spacing={1.2}>
                            {stays.map((stay, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="start"
                                    gap={1.5}
                                    sx={{
                                        bgcolor: index === 0 ? "rgba(255,152,0,0.04)" : "rgba(0,35,102,0.04)",
                                        p: 1.2,
                                        borderRadius: "12px",
                                        border: index === 0 ? "1px solid rgba(255,152,0,0.15)" : "1px solid rgba(0,35,102,0.1)",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            bgcolor: index === 0 ? "rgba(255,152,0,0.08)" : "rgba(0,35,102,0.08)",
                                        }
                                    }}
                                >
                                    {/* Icon or 1st Stay Label */}
                                    <Box sx={{ minWidth: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.3, gap: 1.5 }}>
                                        {index === 0 ? (
                                            <>
                                                <Box
                                                    sx={{
                                                        bgcolor: "#e65100",
                                                        borderRadius: "8px",
                                                        px: 0.8,
                                                        py: 0.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <HotelIcon sx={{ fontSize: 16, color: "#fff" }} />
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '0.55rem',
                                                        color: '#e65100',
                                                        fontWeight: 700,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.3,
                                                        lineHeight: 1,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    1st<br />stay
                                                </Typography>
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    bgcolor: "#002366",
                                                    borderRadius: "8px",
                                                    p: 0.6,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <ArrowDownwardIcon sx={{ color: "#fff", fontSize: 16 }} />
                                            </Box>
                                        )}
                                        {stay.days && (
                                            <Box sx={{
                                                bgcolor: index === 0 ? "rgba(255,152,0,0.12)" : "rgba(0,35,102,0.12)",
                                                borderRadius: "6px",
                                                px: 0.6,
                                                py: 0.3,
                                                textAlign: "center",
                                            }}>
                                                <Typography sx={{
                                                    fontSize: "0.95rem",
                                                    fontWeight: 800,
                                                    color: index === 0 ? "#e65100" : "#002366",
                                                    lineHeight: 1.2,
                                                }}>
                                                    {String(stay.days).replace(/\s*days?/i, '')}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "0.65rem",
                                                    fontWeight: 700,
                                                    color: index === 0 ? "#e65100" : "#002366",
                                                    lineHeight: 1,
                                                    textTransform: "uppercase",
                                                }}>
                                                    days
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ mb: 0.3, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                            <Stack direction="row" spacing={0.4} alignItems="center" sx={{ minWidth: 0, flex: 1, flexWrap: "wrap" }}>
                                                <Typography component="span" variant="caption" sx={{
                                                    fontSize: '0.65rem',
                                                    fontWeight: 800,
                                                    color: index === 0 ? "#e65100" : "#002366",
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.5,
                                                    display: "inline-block",
                                                }}>
                                                    {stay.city}
                                                </Typography>
                                                {stay.zone && (
                                                    <>
                                                        <LocationOnIcon sx={{ fontSize: 10, color: "#94a3b8" }} />
                                                        <Typography component="span" variant="caption" sx={{
                                                            fontSize: '0.65rem',
                                                            color: '#64748b',
                                                            fontWeight: 500,
                                                            wordBreak: 'break-word',
                                                        }}>
                                                            {titleCase(stay.zone)}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Stack>
                                            {stay.start && (
                                                <Stack direction="row" spacing={0.4} alignItems="center" sx={{ flexShrink: 0 }}>
                                                    <CalendarTodayIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                                                    <Typography component="span" variant="caption" sx={{
                                                        fontSize: '0.65rem',
                                                        color: '#64748b',
                                                        fontWeight: 600,
                                                        lineHeight: 1,
                                                    }}>
                                                        {stay.start}
                                                        {stay.end ? ` - ${stay.end}` : ''}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Box>
                                        <Typography variant="body2" display="block" fontWeight={700} sx={{
                                            lineHeight: 1.3,
                                            fontSize: '0.85rem',
                                            mt: 0.2,
                                            color: index === 0 ? "#e65100" : "#1e293b",
                                        }}>
                                            {titleCase(stay.hotel)}
                                        </Typography>
                                        {stay.availability && (
                                            <Box sx={{ mt: 0.5 }}>
                                                {renderRoomTypeBadges(stay.availability, stay.pricing)}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>

                        {/* Camps */}
                        <Box
                            sx={{
                                bgcolor: "rgba(0,35,102,0.03)",
                                p: 1.4,
                                borderRadius: "12px",
                                border: "1px solid rgba(0,35,102,0.08)",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    fontSize: "0.65rem",
                                    color: "#64748b",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    mb: 0.8,
                                }}
                            >
                                Camp Options
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                                {campDisplayEntries.map((camp, index) => {
                                    const campPrice = camp.priceValue ?? (camp.price ? parseFloat(String(camp.price).replace(/[^0-9.]/g, "")) : null);
                                    const hasConverted = camp.available
                                        && Number.isFinite(campPrice)
                                        && currencyRates[selectedCurrency]
                                        && selectedCurrency !== "SAR";
                                    const convertedCampPrice = hasConverted ? campPrice * currencyRates[selectedCurrency] : null;

                                    return (
                                        <Box
                                            key={`${camp.name}-${index}`}
                                            sx={{
                                                bgcolor: "rgba(255,255,255,0.7)",
                                                p: 1,
                                                borderRadius: "10px",
                                                border: "1px solid rgba(148,163,184,0.2)",
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "0.8rem",
                                                            fontWeight: 700,
                                                            color: camp.available ? "#1e293b" : "#94a3b8",
                                                        }}
                                                    >
                                                        {titleCase(camp.name)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            fontWeight: 600,
                                                            color: camp.available ? "#166534" : "#94a3b8",
                                                        }}
                                                    >
                                                        {camp.available
                                                            ? (camp.price ? <>{sarSymbol} {camp.price}</> : <>{sarSymbol} —</>)
                                                            : "Not available"}
                                                    </Typography>
                                                    {hasConverted && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: "block",
                                                                fontSize: "0.73rem",
                                                                fontWeight: 600,
                                                                color: "#64748b",
                                                                mt: 0.2,
                                                            }}
                                                        >
                                                            {(currencySymbols[selectedCurrency] || selectedCurrency)}
                                                            {selectedCurrency === 'JPY'
                                                                ? Math.round(convertedCampPrice).toLocaleString()
                                                                : convertedCampPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Flights */}
                        <Box
                            sx={{
                                bgcolor: "rgba(0,35,102,0.03)",
                                p: 1.4,
                                borderRadius: "12px",
                                border: "1px solid rgba(0,35,102,0.08)",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    fontSize: "0.65rem",
                                    color: "#64748b",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    mb: 0.8,
                                }}
                            >
                                Outbound Flights
                            </Typography>
                            <Box
                                role={hasFlightDetails ? "button" : "presentation"}
                                tabIndex={hasFlightDetails ? 0 : -1}
                                onClick={handleOpenFlights}
                                onKeyDown={(event) => {
                                    if (!hasFlightDetails) return;
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        handleOpenFlights();
                                    }
                                }}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.7)",
                                    p: 1,
                                    borderRadius: "10px",
                                    border: "1px solid rgba(148,163,184,0.2)",
                                    cursor: hasFlightDetails ? "pointer" : "default",
                                    transition: "all 0.2s ease",
                                    "&:hover": hasFlightDetails
                                        ? { bgcolor: "rgba(255,255,255,0.9)", borderColor: "rgba(0,35,102,0.2)" }
                                        : {},
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(0,35,102,0.1)",
                                            borderRadius: "8px",
                                            p: 0.6,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <FlightTakeoffIcon sx={{ fontSize: 16, color: "#002366" }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: hasFlightDetails ? 700 : 600,
                                                color: hasFlightDetails ? "#1e293b" : "#94a3b8",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {outboundDepartureText}
                                        </Typography>
                                    </Box>
                                    {hasFlightDetails && (
                                        <ChevronRightIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    </Stack>

                    <Dialog
                        open={isFlightDialogOpen}
                        onClose={handleCloseFlights}
                        maxWidth="xs"
                        fullWidth
                        PaperProps={{ sx: { borderRadius: "16px", m: 1.5 } }}
                    >
                        <DialogTitle sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#1e293b", py: 1, px: 2 }}>
                            Flight Details
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: 1.5 }}>
                            {hasFlightDetails ? (
                                <Stack spacing={1.2}>
                                    {flightDisplayEntries.map((flight, index) => {
                                        if (!flight.details) return null;
                                        const outbound = flight.details.outbound || {};
                                        const inbound = flight.details.inbound || {};
                                        const hasOutbound = Boolean(outbound.departure || outbound.arrival);
                                        const hasInbound = Boolean(inbound.departure || inbound.arrival);
                                        const renderLeg = (leg, label) => {
                                            const route = [leg.departure, leg.arrival].filter(Boolean).join(' → ');
                                            const airlineInfo = [leg.airline, leg.flightNumber].filter(Boolean).join(' ');
                                            const timeInfo = [leg.dateLine, leg.timeLine].filter(Boolean).join(' · ');
                                            return (
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                                    <Box sx={{
                                                        bgcolor: label === "OUT" ? "rgba(0,35,102,0.1)" : "rgba(100,116,139,0.1)",
                                                        borderRadius: "6px",
                                                        px: 0.6,
                                                        py: 0.3,
                                                        flexShrink: 0,
                                                        mt: "1px",
                                                    }}>
                                                        <Typography variant="caption" sx={{ fontSize: "0.55rem", fontWeight: 800, color: label === "OUT" ? "#002366" : "#64748b", textTransform: "uppercase", lineHeight: 1 }}>
                                                            {label}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
                                                            {route || "—"}
                                                        </Typography>
                                                        {(airlineInfo || timeInfo) && (
                                                            <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#64748b", lineHeight: 1.3, display: "block" }}>
                                                                {[airlineInfo, timeInfo].filter(Boolean).join(' · ')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        };
                                        return (
                                            <Box key={`flight-${index}`} sx={{
                                                bgcolor: "rgba(0,35,102,0.03)",
                                                borderRadius: "10px",
                                                border: "1px solid rgba(0,35,102,0.08)",
                                                p: 1.2,
                                            }}>
                                                {flightDisplayEntries.length > 1 && (
                                                    <Typography variant="caption" sx={{ fontSize: "0.6rem", fontWeight: 800, color: "#002366", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.6 }}>
                                                        Option {index + 1}
                                                    </Typography>
                                                )}
                                                <Stack spacing={0.8}>
                                                    {hasOutbound && renderLeg(outbound, "OUT")}
                                                    {hasInbound && renderLeg(inbound, "IN")}
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No flight details available.
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 0.8 }}>
                            <Button variant="outlined" size="small" onClick={handleCloseFlights} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Footer: Action Button */}
                    <Box sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: "2px solid rgba(0,35,102,0.08)",
                    }}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            disableElevation
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                            href={PackageLink_}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                textTransform: "none",
                                borderRadius: "12px",
                                fontWeight: 700,
                                py: 1.3,
                                fontSize: "0.9rem",
                                bgcolor: "#002366",
                                color: "#fff",
                                boxShadow: "0 4px 12px rgba(0,35,102,0.3)",
                                "&:hover": {
                                    bgcolor: "#001a4d",
                                    boxShadow: "0 6px 16px rgba(0,35,102,0.4)",
                                    transform: "translateY(-2px)",
                                },
                                transition: "all 0.2s ease",
                            }}
                        >
                            Package Link
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Fade>
    );
};

export default PackageCard;
